/* eslint formatjs/no-literal-string-in-jsx: "error" */
import React, { FC, ReactNode } from "react"
import { Title } from "../../../../services/page-title/Title"
import { MultiPathSVG } from "../../SVG"
import styled from "styled-components"
import { Container, FlexContainer } from "@carrotfertility/carotene"
import { useHistory } from "react-router"
import {
  CaseRateRenewalsStatus,
  useGetCaseRateRenewalStatus
} from "../../../carrot-plans/case-rate-renewals/useQueryCaseRateRenewals"
import { AdvancedCheckInWarningQueryString } from "../../../carrot-plans/case-rate-renewals/useCaseRateRenewals"
import { UnauthPreferredLocale } from "../../UnauthPreferredLocale"
import { useFlags } from "launchdarkly-react-client-sdk"
import { useIntl } from "react-intl"

const MainContainer = styled(Container)({
  border: "solid .125rem white",
  width: "600px",
  borderRadius: "8px",
  "@media screen and (max-width: 599px)": {
    minHeight: "100vh",
    border: "solid .125rem #EEDDCD"
  }
})

const LayoutBindHeight = styled.div({
  minHeight: "100vh",
  paddingBottom: "48px"
})

export function FleContinueWrapper({
  paddingTopBottom = "huge",
  paddingLeftRight = "huge",
  children
}: {
  paddingTopBottom?: string
  paddingLeftRight?: string
  children: ReactNode
}): JSX.Element {
  const oneChild = React.Children.toArray(children).length === 1
  return (
    <FlexContainer
      paddingLeftRight={paddingLeftRight}
      paddingTopBottom={paddingTopBottom}
      justifyContent={oneChild ? "flex-end" : "space-between"}
    >
      {children}
    </FlexContainer>
  )
}

type FleLayoutProps = {
  children: ReactNode
  modal?: ReactNode
  /* Bottom content is really just for avoiding a lot of AboutYou changes.
  Ideally this layout would be tweaked if we were to also rewrite AboutYou */
  bottomContent?: ReactNode
  closeButton?: boolean
}

/**
 * @deprecated
 */
const LegacyFleLayout: FC<FleLayoutProps> = ({ modal, ...otherProps }) => {
  const intl = useIntl()
  return (
    <LayoutBindHeight>
      <Title title={intl.formatMessage({ defaultMessage: "Welcome to Carrot" })} />
      <LogoHeaderBar />
      <MainFleSection {...otherProps} />
      {modal ? <div>{modal}</div> : null}
    </LayoutBindHeight>
  )
}

const LogoHeaderBar = () => (
  <div className="dn flex-ns justify-center" style={{ marginBottom: "32px" }}>
    <div
      className="ph4 w-100"
      style={{
        maxWidth: "1224px"
      }}
    >
      <MultiPathSVG iconName="carrot-white" />
    </div>
  </div>
)

type MainFleSectionProps = {
  children: ReactNode
  bottomContent?: ReactNode
  closeButton?: boolean
}

function CloseButton(): JSX.Element {
  const history = useHistory()
  const { data, showAdvancedCheckInFeatures } = useGetCaseRateRenewalStatus()
  const intl = useIntl()

  async function onClick() {
    if (data === CaseRateRenewalsStatus.GATED) {
      history.push("/refresh-carrot-plan-leave-warning")
    } else if (showAdvancedCheckInFeatures) {
      history.push(`/?step=${AdvancedCheckInWarningQueryString}`)
    } else {
      history.push("/")
    }
  }

  return (
    <button
      className="input-reset pointer pa0 bw0 bg-transparent black-60 hover-black animate-all fr"
      onClick={onClick}
    >
      <img alt={intl.formatMessage({ defaultMessage: "close" })} src="/images/icn-close-modal.svg" />
    </button>
  )
}

function MainFleSection({ children, bottomContent, closeButton }: MainFleSectionProps): JSX.Element {
  const { showPreferredLocaleSelect } = useFlags()
  return (
    <FlexContainer justifyContent="center" width="100%">
      <MainContainer>
        <FlexContainer height="100%" direction="column" justifyContent="space-between">
          <div>
            <FlexContainer justifyContent="flex-end" padding="medium">
              {showPreferredLocaleSelect && <UnauthPreferredLocale showModal={true} />}
              {closeButton && <CloseButton />}
            </FlexContainer>
            <Container paddingLeftRight="huge">{children}</Container>
          </div>
          {bottomContent}
        </FlexContainer>
      </MainContainer>
    </FlexContainer>
  )
}

export { LegacyFleLayout }
